import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { products, categories, getProductsByCategory } from '@/mocks/products';
import { articles, articleCategories } from '@/mocks/articles';

export default function Category() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'products';
  const [activeTab, setActiveTab] = useState<'products' | 'articles'>(initialTab as 'products' | 'articles');
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredProducts = useMemo(() => getProductsByCategory(activeCategory), [activeCategory]);

  const filteredArticles = useMemo(() => {
    if (activeCategory === 'all') return articles;
    const catName = categories.find((c) => c.slug === activeCategory)?.name;
    if (!catName) return articles;
    if (catName === '其他时令') return articles.filter((a) => a.tags.some((t) => ['芒果', '水蜜桃', '猕猴桃', '时令'].includes(t)));
    return articles.filter((a) => a.tags.some((t) => catName.includes(t) || t.includes(catName)));
  }, [activeCategory]);

  const handleTabSwitch = (tab: 'products' | 'articles') => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEO
        title="瓜果分类"
        description="瓜瓜基地精选时令瓜果分类展示，包含麒麟西瓜、西州蜜哈密瓜、玉菇甜瓜、绿宝石香瓜等各类优质瓜果，产地直发新鲜到家。"
        keywords="瓜瓜基地瓜果分类,瓜瓜基地西瓜,瓜瓜基地哈密瓜,瓜瓜基地香瓜,瓜瓜基地时令水果"
        canonicalUrl="https://guaguabase.com/category"
      />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '瓜果分类 - 瓜瓜基地',
        description: '瓜瓜基地精选时令瓜果分类展示',
        url: 'https://guaguabase.com/category',
      }} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '瓜果分类' }]} />
          </div>
        </div>

        <div className="bg-background-100 py-8 md:py-10">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto text-center">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">瓜果分类</h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
              精选全国优质产区时令瓜果，总有一款让你心动
            </p>
          </div>
        </div>

        <div className="sticky top-16 md:top-18 z-40 bg-background-50 border-b border-background-200">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
                <button
                  onClick={() => handleTabSwitch('products')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === 'products'
                      ? 'bg-primary-500 text-background-50 shadow-md'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  瓜果产品
                </button>
                <button
                  onClick={() => handleTabSwitch('articles')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === 'articles'
                      ? 'bg-primary-500 text-background-50 shadow-md'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  知识分享
                </button>
              </div>

              {activeTab === 'articles' && (
                <div className="hidden md:flex items-center gap-1.5 flex-wrap">
                  {articleCategories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat === '全部文章' ? 'all' : cat === '西瓜' ? 'watermelon' : cat === '哈密瓜' ? 'hami-melon' : cat === '香瓜' ? 'cantaloupe' : 'seasonal')}
                      className="text-xs px-3 py-1 rounded-full text-foreground-500 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {activeTab === 'products' && (
          <section className="py-8 md:py-10 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <div className="flex items-center gap-2 mb-6 md:mb-8 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              {filteredProducts.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无产品，请查看其他分类</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop="">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className="group bg-background-50 rounded-2xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative aspect-square overflow-hidden bg-background-100">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {product.badge && (
                          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
                            {product.badge}
                          </span>
                        )}
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-heading text-lg text-foreground-900">{product.name}</h3>
                          <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {product.origin}
                          </span>
                        </div>
                        <p className="text-sm text-foreground-500 mb-3 line-clamp-2 leading-relaxed">{product.description}</p>
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {product.features.map((feat, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-accent-50 text-accent-700">{feat}</span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xl font-bold text-primary-600">¥{product.price}</span>
                            <span className="text-xs text-foreground-400 ml-1">/{product.unit}</span>
                          </div>
                          <span className="text-xs px-3 py-1.5 rounded-full bg-accent-50 text-accent-600 font-medium whitespace-nowrap">
                            产地直发
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'articles' && (
          <section className="py-8 md:py-10 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              {filteredArticles.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">暂无相关文章</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group bg-background-50 rounded-2xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden bg-background-100">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-accent-50 text-accent-700">{article.category}</span>
                          <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
                        </div>
                        <h3 className="font-heading text-base md:text-lg text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.summary}</p>
                        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-background-100">
                          <span className="text-xs text-foreground-400">{article.author}</span>
                          <span className="text-xs text-foreground-300">·</span>
                          <span className="text-xs text-foreground-400">{article.publishDate}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}